import { nonUndefined, onlyUnique } from "Utility";
import {
  getTournamentMatchIds,
  getTournamentMatchTitle,
  Tournament,
  TournamentStatus,
} from "model/Tournament/Tournament";
import { RootState } from "app/store";
import { Stage } from "model/Tour/Stage";
import {
  getTourShortTitle,
  getTourTitle,
  getTourTournamentIds,
  tourContainsTournament,
} from "model/Tour/Tour";
import {
  selectTournament,
  selectTournamentEndTime,
  selectTournaments,
  selectTournamentsByYear,
} from "./Tournament";
import { selectMatches } from "./Match";
import { getSegmentMatchIds } from "model/Tournament/Segment/TournamentSegment";
import { matchHasStarted } from "model/Match/MatchStatus";
import { getTimeMs } from "TimeHelper";
import { selectTournamentLoaded } from "./TournamentLoading";

export const selectAllTours = (state: RootState) => {
  return Object.values(state.tour.tours);
};

export const selectTour = (state: RootState, tourId: string | undefined) => {
  if (!tourId) {
    return undefined;
  }

  return state.tour.tours[tourId];
};

export const selectTours = (state: RootState, tourIds: string[]) => {
  return nonUndefined(tourIds.map((id) => selectTour(state, id)));
};

export const selectTourByName = (
  state: RootState,
  tourName: string | undefined
) => {
  if (!tourName) {
    return undefined;
  }

  return selectAllTours(state).find((t) => t.name === tourName);
};

export const selectTourFilterOptions = (
  state: RootState,
  year: string,
  includeNonTourTournaments: boolean = false
) => {
  const tours = selectToursByYear(state, year);
  const tournaments = includeNonTourTournaments
    ? selectNonTourTournaments(state, year)
    : [];

  return tours
    .flatMap((tour) => tour.name)
    .concat(tournaments.flatMap((t) => t.name))
    .filter(onlyUnique);
};

export const selectTourImageId = (state: RootState, tourId: string) => {
  return selectTour(state, tourId)?.imageIdOverride ?? tourId;
};

export const selectTourName = (state: RootState, tourId: string) => {
  return selectTour(state, tourId)?.name;
};

export const selectTourTrigram = (state: RootState, tourId: string) => {
  return selectTour(state, tourId)?.trigram;
};

export const selectStageFilterOptions = (
  state: RootState,
  tourName: string | undefined
) => {
  const tour = selectTourByName(state, tourName);
  if (!tour) {
    return undefined;
  }

  return nonUndefined(tour.stages.map((s) => s.name));
};

export const selectNonTourTournaments = (state: RootState, year: string) => {
  const tours = selectToursByYear(state, year);
  const tourTournamentIds = tours.flatMap((tour) => getTourTournamentIds(tour));
  const tournaments = selectTournamentsByYear(state, year);
  return tournaments.filter(
    (tournament) => !tourTournamentIds.includes(tournament.id)
  );
};

export const selectToursByYear = (
  state: RootState,
  year: string | undefined
) => {
  const yearNum = !!year ? parseInt(year) : undefined;
  if (!yearNum || isNaN(yearNum)) {
    return selectAllTours(state);
  }

  return selectAllTours(state).filter((t) => {
    const startYear = new Date(t.startTimeMs).getFullYear();
    const endYear = new Date(t.endTimeMs).getFullYear();
    return yearNum >= startYear && yearNum <= endYear;
  });
};

export const selectTourIdsFromFilters = (
  state: RootState,
  year: string | undefined,
  tourOrTournamentName: string | undefined
) => {
  const tours = selectToursByYear(state, year);
  if (!!tourOrTournamentName) {
    const tour = tours.find((t) => t.name === tourOrTournamentName);
    if (!tour) {
      return [];
    }

    return [tour.id];
  }

  return tours.map((t) => t.id).filter(onlyUnique);
};

export const selectTourMatchIds = (state: RootState, tourId: string) => {
  return selectTour(state, tourId)?.stages.flatMap((s) =>
    selectStageMatchIds(state, s)
  );
};

export const selectStageMatchIds = (state: RootState, stage: Stage) => {
  const tournaments = selectTournaments(state, stage.tournamentIds);
  return tournaments.flatMap((t) => getTournamentMatchIds(t));
};

export const selectStageById = (
  state: RootState,
  tourId: string,
  stageId: string
) => {
  return selectTour(state, tourId)?.stages.find((s) => s.id === stageId);
};

// todo
export const selectAllYears = (state: RootState) => {
  return ["2025", "2024", "2023"];
  // return selectAllTours(state).map((t) => t.year);
};

export const selectStagesByName = (
  state: RootState,
  year: string | undefined,
  stageName: string | undefined
) => {
  const tours = selectToursByYear(state, year);
  const stages = tours.flatMap((t) => t.stages);
  if (!stageName) {
    return stages;
  }
  return stages.filter((s) => s.name === stageName);
};

// todo - rework this to just use times?t
export const selectTournamentStatus = (
  state: RootState,
  tournament: Tournament
): TournamentStatus => {
  if (!selectTournamentLoaded(state, tournament.id)) {
    return tournament.endTimeMs < getTimeMs() ? "Completed" : "Upcoming";
  }

  const matchIds = getTournamentMatchIds(tournament);
  const matches = selectMatches(state, matchIds);

  if (
    getTimeMs() > tournament.endTimeMs &&
    matches.length > 0 &&
    tournament.segments.every((s) => getSegmentMatchIds(s).length > 0) &&
    matches.every((m) => m.isComplete)
  ) {
    return "Completed";
  }

  return matches.some(matchHasStarted) ? "Ongoing" : "Upcoming";
};

export const selectStageEndTimeById = (
  state: RootState,
  tourId: string,
  stageId: string
) => {
  const stage = selectStageById(state, tourId, stageId);
  if (!stage) {
    return undefined;
  }

  return selectStageEndTime(state, stage);
};

export const selectStageEndTime = (state: RootState, stage: Stage) => {
  const tournamentEndTimes = stage.tournamentIds.map((id) =>
    selectTournamentEndTime(state, id)
  );

  const definedTournamentEndTimes = tournamentEndTimes.filter(
    (t) => !!t
  ) as number[];
  if (definedTournamentEndTimes.length === 0) {
    return undefined;
  }

  return Math.max(...definedTournamentEndTimes);
};

export const selectMatchTitle = (
  state: RootState,
  matchId: string,
  tournamentId: string,
  includeMatchPart: boolean
) => {
  const tournament = selectTournament(state, tournamentId);
  if (!tournament) {
    return undefined;
  }

  return getTournamentMatchTitle(tournament, matchId, includeMatchPart);
};

export const selectTourIdsFromTournamentId = (
  state: RootState,
  tournamentId: string
) => {
  const tours = selectAllTours(state);
  return tours
    .filter((t) => tourContainsTournament(t, tournamentId))
    .map((t) => t.id);
};

export const selectDisplayTourForTournamentId = (
  state: RootState,
  tournamentId: string
) => {
  const tours = selectAllTours(state);
  return tours.find(
    (t) => t.groupTournaments && tourContainsTournament(t, tournamentId)
  );
};

export const selectTourShortTitleDisplayForTournamentId = (
  state: RootState,
  tournamentId: string
) => {
  const tournamentTour = selectDisplayTourForTournamentId(state, tournamentId);
  if (!tournamentTour) {
    return undefined;
  }

  return getTourShortTitle(tournamentTour, tournamentId);
};

export const selectTourTitleDisplayForTournamentId = (
  state: RootState,
  tournamentId: string
) => {
  const tournamentTour = selectDisplayTourForTournamentId(state, tournamentId);
  if (!tournamentTour) {
    return undefined;
  }

  return getTourTitle(tournamentTour, tournamentId);
};

export const selectTourNameDisplayForTournamentId = (
  state: RootState,
  tournamentId: string
) => {
  const tournamentTour = selectDisplayTourForTournamentId(state, tournamentId);
  return tournamentTour?.name;
};

export const selectTourTrigramDisplayForTournamentId = (
  state: RootState,
  tournamentId: string
) => {
  const tournamentTour = selectDisplayTourForTournamentId(state, tournamentId);
  return tournamentTour?.trigram;
};
